/**
 * PasswordInput — any password (or masked-sensitive) field with a show/hide
 * eye toggle (task #50, 2026-08-11 owner directive: "anytime the user is
 * typing a password, there should be a little icon off to the right … to see
 * the password in text").
 *
 * Styling-agnostic on purpose: pass the surface's own styled input component
 * via `inputComponent` (AuthFormSection's Input, Wallet's FormInput, …) so
 * every field keeps its exact look — this wrapper only adds the overlay
 * button and flips type between 'password' and 'text'.
 */
import React, { useState, forwardRef } from 'react';

type AnyInputComponent =
  | React.ComponentType<React.InputHTMLAttributes<HTMLInputElement> & { ref?: any }>
  | 'input';

export interface PasswordInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** The surface's own (styled) input component; plain <input> by default. */
  inputComponent?: AnyInputComponent;
}

const EyeIcon: React.FC<{ off?: boolean }> = ({ off }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" />
    <circle cx="12" cy="12" r="2.6" />
    {off && <line x1="4" y1="20" x2="20" y2="4" />}
  </svg>
);

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ inputComponent, style, ...props }, ref) => {
    const [visible, setVisible] = useState(false);
    const C: any = inputComponent || 'input';
    return (
      <span style={{ position: 'relative', display: 'block', width: '100%' }}>
        <C
          {...props}
          ref={ref}
          type={visible ? 'text' : 'password'}
          // The revealed password MUST render byte-exact (owner report
          // 2026-08-12): the brand's global `input { text-transform:
          // lowercase !important }` made the preview lie about capitals — a
          // user reset their password to all-lowercase believing capitals
          // didn't work. An inline style cannot beat !important, so the
          // exception lives in global.css keyed on this attribute.
          data-exact-case="true"
          style={{
            ...style,
            width: '100%',
            boxSizing: 'border-box',
            paddingRight: '2.7rem',
            textTransform: 'none',
          }}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'hide password' : 'show password'}
          title={visible ? 'hide password' : 'show password'}
          /* Not in the tab order: keyboard users hit it via the accessible
             name with a screen reader's forms mode; keeping Tab flowing
             email → password → submit matches every mainstream login. */
          tabIndex={-1}
          style={{
            position: 'absolute',
            right: '0.65rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            padding: '0.25rem',
            cursor: 'pointer',
            color: 'var(--brand-text-muted)',
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: 1,
          }}
        >
          <EyeIcon off={!visible} />
        </button>
      </span>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
